import React, { useRef, useCallback } from 'react'
import { useField } from 'formik'
import { Dropdown } from 'primereact/dropdown'
import { useTranslation } from 'react-i18next'

const sharedPt = lang => ({
  dropdownicon: { className: 'h-[10px] w-[10px] md:h-[12px] md:w-[12px]' },
  header: { className: 'p-[10px]' },
  item: { className: 'capitalize !outline-none !whitespace-normal !break-words' },
  filterinput: { className: 'h-[30px] !text-[10px] md:!text-[12px]' },
  filtericon: { className: lang === 'en' ? '!right-[1rem] !left-auto' : '!left-[1rem] !right-auto' },
  input: {
    className: `!text-[10px] md:!text-[12px] !py-[6px] ${
      lang === 'en' ? '!pl-[8px] !pr-[3.2rem]' : '!pr-[8px] !pl-[3.2rem]'
    }`,
  },
  itemgrouplabel: {
    className: '!text-primary !font-semibold capitalize text-[10px] md:text-[12px] !whitespace-normal !break-words',
  },
  itemlabel: { className: '!text-[10px] md:!text-[12px] !whitespace-normal !break-words !leading-snug' },
  clearicon: {
    className: `!text-dark ${lang === 'en' ? '!right-[2.3rem] !left-auto' : '!left-[2.3rem] !right-auto'}`
  },
  list: { className: '!overflow-x-hidden' },
})

const isGrouped = (options = []) =>
  Array.isArray(options) && options.length > 0 && options[0].options

const hasTemplateOptions = (options = []) =>
  Array.isArray(options) && options.some(o => o?.template);

const eligibilityColorMap = {
  'bg-green-200': '#bbf7d0',
  'bg-red-200': '#fecaca',
};

const templateItemRenderer = (option) => {
  if (!option) return null;
  const bgColor = eligibilityColorMap[option.eligibilityClass] || 'transparent';
  return (
    <div
      style={{
        backgroundColor: bgColor,
        margin: '-0.571rem -1rem',
        padding: '0.571rem 1rem',
        width: 'calc(100% + 2rem)',
        display: 'block',
        boxSizing: 'border-box',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      }}
    >
      {option.template ?? option.label}
    </div>
  );
};

const useDropdownPanelStyle = () => {
  const wrapperRef = useRef(null)
  const getPanelStyle = useCallback(() => {
    if (wrapperRef.current) {
      const w = wrapperRef.current.offsetWidth
      return { width: `${w}px` }
    }
    return {}
  }, [])
  return { wrapperRef, getPanelStyle }
}

const FormikDropdownField = ({
  label = '',
  name = '',
  className = '',
  options = [],
  optionLabel = 'label',
  optionValue = 'value',
  placeholder,
  parentClass = '',
  filter = false,
  showClear = false,
  filterPlaceholder = 'Search',
  hideErrorText = false,
  itemTemplate,
  ...props
}) => {
  const [field, meta] = useField(name)
  const { t, i18n } = useTranslation()
  const { wrapperRef, getPanelStyle } = useDropdownPanelStyle()

  const isInvalid = meta.error && meta.touched;

  return (
    <div className={`flex flex-col gap-1 w-full mb-1 ${parentClass}`}>
      {label && (
        <label htmlFor={name} className="input-label">
          {label}
        </label>
      )}

      <div ref={wrapperRef} className="w-full">
        <Dropdown
          options={options}
          optionLabel={optionLabel}
          optionValue={optionValue}
          {...(isGrouped(options) && {
            optionGroupLabel: 'label',
            optionGroupChildren: 'options',
          })}
          placeholder={placeholder}
          filter={filter}
          filterPlaceholder={t(filterPlaceholder)}
          className={`!w-full ${className} input-field !p-0 ${isInvalid && hideErrorText ? "!border-red-500" : ""}`}
          pt={sharedPt(i18n.language)}
          panelStyle={getPanelStyle()}
          showClear={showClear}
          itemTemplate={itemTemplate || (hasTemplateOptions(options) ? templateItemRenderer : undefined)}
          {...field}
          {...props}
        />
      </div>

      {isInvalid && !hideErrorText && (
        <p className="text-red-500 text-sm">{meta.error}</p>
      )}
    </div>
  )
}

const StandaloneDropdownField = ({
  label = '',
  options = [],
  optionLabel = 'label',
  optionValue = 'value',
  parentClass = '',
  placeholder = '',
  className = '',
  value = null,
  filter = false,
  ptOptions = null,
  filterPlaceholder = 'Search',
  showClear = false,
  NavigationIcon,
  onIconClick = () => { },
  handleChange = () => { },
  error = "",
  itemTemplate,
  ...props
}) => {
  const { t, i18n } = useTranslation()
  const { wrapperRef, getPanelStyle } = useDropdownPanelStyle()

  return (
    <div className={`flex flex-col gap-1 w-full mb-1 ${className}`}>
      {label && (
        <div className="flex items-center justify-between">
          <label className="input-label">{label}</label>
          {NavigationIcon && (
            <NavigationIcon
              size={17}
              className="text-primary cursor-pointer"
              onClick={onIconClick}
            />
          )}
        </div>
      )}

      <div ref={wrapperRef} className="w-full">
        <Dropdown
          options={options}
          optionLabel={optionLabel}
          optionValue={optionValue}
          {...(isGrouped(options) && {
            optionGroupLabel: 'label',
            optionGroupChildren: 'options',
          })}
          placeholder={placeholder}
          value={value}
          filter={filter}
          filterPlaceholder={t(filterPlaceholder)}
          onChange={handleChange}
          className={`!w-full input-field ${className} !p-0 ${error ? "!border-red-500" : ""}`}
          pt={ptOptions ? ptOptions : sharedPt(i18n.language)}
          panelStyle={getPanelStyle()}
          showClear={showClear}
          itemTemplate={itemTemplate || (hasTemplateOptions(options) ? templateItemRenderer : undefined)}
          {...props}
        />
      </div>

      {error && (
        <p className="text-red-500 text-sm">{error}</p>
      )}
    </div>
  )
}

const DropdownField = props => {
  const { useFormik = true } = props
  return useFormik ? (
    <FormikDropdownField {...props} />
  ) : (
    <StandaloneDropdownField {...props} />
  )
}

export default DropdownField
